/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CaptureRequest, LocalFileResourcePath } from './type'
import { PluginListenerHandle, registerPlugin, Capacitor } from '@capacitor/core'
import type { ModalCamera as ModalCameraType } from '@/app/camera/type'
import { isApp } from '@/index'
import { Homecheck } from 'capacitor-homecheck'
import { Base64String } from 'capacitor-voice-recorder'
import { syncQueue } from '@/app/sync'
import { ref } from 'vue'

// 카메라가 의도치 않게 종료됐을 때 사용자 안내 모달을 띄우기 위한 전역 상태 (null이면 미표시)
// reason 'restart': 촬영 중 앱 강제 종료(process death) 후 재시작 시점에 감지
// reason 'error': 사용자가 취소하지 않았는데 촬영이 실패/중단된 경우 (기기 호환·메모리 문제 등)
export const cameraCrashNotice = ref<{
  reason: 'restart' | 'error'
  photoRecovered: boolean
} | null>(null)

// 사용자가 직접 닫은(취소) 에러인지 판별 - 취소는 안내 모달 표시 대상이 아님
function isCancellationError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes('취소') || message.toLowerCase().includes('cancel')
}

// 촬영 시작 후 이 시간 안에 "취소"로 돌아오면 사용자 취소가 아니라
// 카메라 앱의 비정상 종료로 판정한다 (조준→닫기까지는 이보다 오래 걸림).
// 안드로이드 촬영은 기본 카메라 앱을 인텐트로 띄우는 방식이라, 일부 기기에서
// 카메라 앱이 크래시/강제종료되면 RESULT_CANCELED(→"취소" reject)로 돌아온다.
const QUICK_CANCEL_THRESHOLD_MS = 3000

/**
 * 카메라 액티비티가 화면을 점유한 시간(우리 앱이 hidden이던 시간)을 측정합니다.
 * "호출부터 경과시간"은 카메라 앱 콜드스타트·권한 프롬프트 시간이 섞여 오판 소지가 있어,
 * 사용자 취소/카메라 앱 즉사 판별은 실제 카메라가 보였던 시간을 기준으로 합니다.
 */
function createHiddenTimeTracker(): { stop: () => void; getHiddenMs: () => number | null } {
  let hiddenTotal = 0
  let hiddenSince: number | null = null
  let observed = false
  const onVisibilityChange = (): void => {
    if (document.hidden) {
      observed = true
      hiddenSince = Date.now()
    } else if (hiddenSince !== null) {
      hiddenTotal += Date.now() - hiddenSince
      hiddenSince = null
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  // 진행 중인 hidden 구간도 합산해 반환 (reject가 복귀 직전에 도착해도 누락 없음)
  const getHiddenMs = (): number | null => {
    if (!observed) return null // 미발화 기기 - 호출자가 경과시간으로 폴백
    return hiddenTotal + (hiddenSince !== null ? Date.now() - hiddenSince : 0)
  }
  return {
    stop: (): void => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    },
    getHiddenMs,
  }
}

// 플랫폼별로 다른 플러그인 사용
export const modalCamera =
  Capacitor.getPlatform() === 'ios'
    ? (Homecheck as unknown as ModalCameraType) // iOS: capacitor-homecheck 플러그인 사용
    : registerPlugin<ModalCameraType>('ModalCamera') // Android: 기존 ModalCamera 플러그인 사용

// Process death 복구를 위한 pending capture 상태 관리
const PENDING_CAPTURE_KEY = 'zentrix_pending_capture'
const RECOVERED_CAPTURE_KEY = 'zentrix_recovered_capture'

interface PendingCapture {
  shapeDatabaseId: string
  fileName: string
  timestamp: number
}

export interface RecoveredCapture {
  resourcePath: string
  shapeDatabaseId: string
  timestamp: number
}

// pending 상태 저장 (localStorage 오류 시 무시 - 촬영 자체를 막으면 안됨)
function savePendingCapture(shapeDatabaseId: string, fileName: string): void {
  try {
    const pending: PendingCapture = { shapeDatabaseId, fileName, timestamp: Date.now() }
    localStorage.setItem(PENDING_CAPTURE_KEY, JSON.stringify(pending))
  } catch {
    /* QuotaExceededError 등 무시 */
  }
}

// pending 상태 제거
export function clearPendingCapture(): void {
  localStorage.removeItem(PENDING_CAPTURE_KEY)
}

// pending 상태 조회 (5분 이내만 유효)
export function getPendingCapture(): PendingCapture | null {
  const raw = localStorage.getItem(PENDING_CAPTURE_KEY)
  if (!raw) return null
  try {
    const pending: PendingCapture = JSON.parse(raw)
    if (Date.now() - pending.timestamp > 5 * 60 * 1000) {
      clearPendingCapture()
      return null
    }
    return pending
  } catch {
    clearPendingCapture()
    return null
  }
}

// 복구된 사진 정보 저장 (localStorage 오류 시 무시)
function saveRecoveredCapture(resourcePath: string, shapeDatabaseId: string): void {
  try {
    const data: RecoveredCapture = { resourcePath, shapeDatabaseId, timestamp: Date.now() }
    localStorage.setItem(RECOVERED_CAPTURE_KEY, JSON.stringify(data))
  } catch {
    /* QuotaExceededError 등 무시 */
  }
}

/** 해당 shapeDatabaseId에 대한 복구된 사진이 있으면 반환하고 소비(삭제)한다. (1시간 유효) */
export function consumeRecoveredCapture(shapeDatabaseId: string): RecoveredCapture | null {
  const raw = localStorage.getItem(RECOVERED_CAPTURE_KEY)
  if (!raw) return null
  try {
    const data: RecoveredCapture = JSON.parse(raw)
    if (Date.now() - data.timestamp > 60 * 60 * 1000) {
      localStorage.removeItem(RECOVERED_CAPTURE_KEY)
      return null
    }
    if (data.shapeDatabaseId === shapeDatabaseId) {
      localStorage.removeItem(RECOVERED_CAPTURE_KEY)
      return data
    }
    return null
  } catch {
    localStorage.removeItem(RECOVERED_CAPTURE_KEY)
    return null
  }
}

/**
 * 사진을 촬영하기 위해 capture 함수를 호출합니다.
 * @param title 촬영 화면에 표시될 제목
 * @param fileName 저장될 파일명
 * @param shapeDatabaseId 사진이 연결될 도형 ID (로그를 위해 필요)
 * @returns 촬영된 사진의 리소스 경로
 */
export const capture = async (
  title: string,
  fileName: string,
  shapeDatabaseId: string = '',
  options: { skipQuickCancelCheck?: boolean } = {},
): Promise<string> => {
  const request: CaptureRequest = {
    title,
    fileName,
    shapeDatabaseId,
    methods: [
      {
        guideText: '',
        isGuidelineVisible: false,
      },
    ],
    width: 1920,
    height: 1440,
  }

  // 새 촬영 시작 시 이전 복구 플래그 리셋
  recoveryHandled = false
  // Process death 대비: 촬영 전 pending 상태 저장
  savePendingCapture(shapeDatabaseId, fileName)

  // 촬영 동안 우리 앱은 백그라운드로 밀리는데, 이때 업로드(WebP/base64 변환)가 돌면
  // 메모리 압박으로 앱 또는 카메라 앱이 강제 종료될 수 있어 동기화 큐를 멈춘다
  syncQueue.pause()
  try {
    const startedAt = Date.now()
    const hiddenTracker = createHiddenTimeTracker()
    try {
      const { value } = await modalCamera.capture({
        ...request,
      })
      clearPendingCapture()
      if (value.length === 0) {
        throw new Error('촬영된 사진이 없습니다.')
      }
      return value[0].resourcePath
    } catch (error) {
      clearPendingCapture()
      const elapsed = Date.now() - startedAt
      // 판별 기준: 카메라가 실제로 화면에 떠 있던 시간 (visibilitychange 미발화 시 경과시간 폴백)
      const cameraShownMs = hiddenTracker.getHiddenMs() ?? elapsed
      const cancelled = isCancellationError(error)
      // 사용자가 직접 닫은 게 아닌데 카메라가 닫혔거나(에러),
      // 뜨자마자 "취소"로 돌아왔다면(카메라 앱 비정상 종료) 조치 안내 모달 표시
      if (
        !cancelled ||
        (!options.skipQuickCancelCheck && cameraShownMs < QUICK_CANCEL_THRESHOLD_MS)
      ) {
        console.warn(
          `[Camera] 촬영 비정상 종료 감지 (표시 ${cameraShownMs}ms/경과 ${elapsed}ms):`,
          error instanceof Error ? error.message : error,
        )
        cameraCrashNotice.value = { reason: 'error', photoRecovered: false }
      }
      throw error
    } finally {
      hiddenTracker.stop()
    }
  } finally {
    syncQueue.resume()
  }
}

// 연속 촬영 사이 대기 - 즉시 재호출하면 네이티브가 "CAMERA IS USING"으로 거부할 수 있다
const SEQUENCE_RELAUNCH_DELAY_MS = 500

/**
 * 사진을 count장 연속 촬영합니다. 기본 카메라 인텐트는 호출 1번에 1장이라 장마다 카메라를 다시 띄웁니다.
 * (네이티브 methods를 여러 개 넘기면 인텐트 모드에서 다음 촬영을 띄우지 않아 call이 끝나지 않음 - 금지)
 * - 장마다 onEach를 끝낸 뒤 다음 촬영: 다음 촬영 중 앱이 강제 종료돼도 앞 장은 이미 저장돼 있다
 * - 전체를 동기화 큐 pause 하나로 감싼다: 장 사이에 앞 장 업로드(WebP 변환)가 다음 카메라와 겹치지 않게
 * - 2장째부터는 quick-cancel 판정을 끈다: 카메라 앱은 앞 장에서 정상 동작했고, 바로 닫는 것도 정상 취소
 * 중간 취소·실패는 그대로 throw - 호출자는 onEach 호출 횟수로 몇 장 저장됐는지 판단한다.
 * @param count 촬영할 장수
 * @param title 촬영 화면 제목
 * @param fileName 저장 파일명
 * @param shapeDatabaseId 사진이 연결될 도형 ID
 * @param onEach 한 장 찍을 때마다 호출 (index는 0부터)
 * @param onBetween 다음 촬영 전 대기 시작 시 호출 (로딩 표시 등)
 */
export const captureSequence = async (
  count: number,
  title: string,
  fileName: string,
  shapeDatabaseId: string,
  onEach: (resourcePath: string, index: number) => Promise<void>,
  onBetween?: () => void,
): Promise<void> => {
  syncQueue.pause()
  try {
    for (let i = 0; i < count; i++) {
      if (i > 0) {
        onBetween?.()
        await new Promise(resolve => setTimeout(resolve, SEQUENCE_RELAUNCH_DELAY_MS))
      }
      const resourcePath = await capture(title, fileName, shapeDatabaseId, {
        skipQuickCancelCheck: i > 0,
      })
      await onEach(resourcePath, i)
    }
  } finally {
    syncQueue.resume()
  }
}

// 플랫폼에 관계없이 convertFileToWebp 함수 제공
export const convertFileToWebp = async (request: {
  resourcePath: LocalFileResourcePath
}): Promise<{ resourcePath: LocalFileResourcePath; value: Base64String }> => {
  const newResourcePath = request.resourcePath
    .replace('android-local-file://', 'file://')
    .replace('android-file://', 'file://')
    .replace('local-file://', 'file://') as LocalFileResourcePath
  return modalCamera.convertFileToWebp({ resourcePath: newResourcePath })
}

// 카메라 이벤트 타입 정의 업데이트
interface CameraEvent {
  type: string
  event_type: string // 이벤트 타입 추가
  shape_id: string
  file_path: string
}

// 이벤트 리스너 핸들 저장
let cameraEventListener: PluginListenerHandle | null = null

// Process death 복구 이벤트 리스너 핸들
let captureRecoveryListener: PluginListenerHandle | null = null

// 카메라 이벤트 리스너 등록
export async function registerCameraEventListener(): Promise<void> {
  if (!isApp()) return
  // 앱 시작 시점에 pending capture가 남아 있으면 직전 세션에서
  // 카메라 촬영 중 프로세스가 강제 종료된 것 (정상 종료 시엔 항상 clear됨)
  // 복구 리스너/pull 체크가 pending을 지우기 전에 먼저 읽어둔다
  const crashedCapture = getPendingCapture()
  // 기존 리스너가 있으면 제거
  if (cameraEventListener) {
    await cameraEventListener.remove()
  }

  // 네이티브 이벤트 리스너 등록
  try {
    cameraEventListener = await modalCamera.addListener(
      'modalCameraEvent',
      (event: CameraEvent) => {
        handleCameraEvent(event)
      },
    )
  } catch (error) {
    console.error('Failed to register camera event listener:', error)
  }

  // Process death 복구 리스너 등록
  if (captureRecoveryListener) {
    await captureRecoveryListener.remove()
  }
  try {
    captureRecoveryListener = await modalCamera.addListener(
      'captureRecovered',
      (event: { resourcePath: string; shapeDatabaseId: string; recovered: boolean }) => {
        handleCaptureRecovery(event)
      },
    )
  } catch (error) {
    console.error('Failed to register capture recovery listener:', error)
  }

  // 글로벌 이벤트 핸들러 등록 (네이티브에서 직접 호출용)
  if (typeof window !== 'undefined') {
    ;(window as any).App = {
      ...(window as any).App,
      logCameraEvent: handleCameraEvent,
    }
  }

  // Pull 방식 복구 체크: 네이티브 이벤트 타이밍에 의존하지 않는 확정적 복구
  try {
    const result = await modalCamera.checkRecoveredCapture()
    if (result.recovered && result.resourcePath) {
      handleCaptureRecovery({
        resourcePath: result.resourcePath,
        shapeDatabaseId: result.shapeDatabaseId ?? '',
        recovered: true,
      })
    }
  } catch {
    // 구버전 네이티브 또는 iOS에서는 미지원 - 이벤트 방식으로 fallback
  }

  // 촬영 중 강제 종료 흔적이 있으면 사용자 안내 모달 표시
  // (recoveryHandled: 위 복구 절차에서 촬영된 사진을 건져냈는지 여부)
  if (crashedCapture) {
    clearPendingCapture()
    cameraCrashNotice.value = { reason: 'restart', photoRecovered: recoveryHandled }
  }
}

// push(이벤트) + pull(checkRecoveredCapture) 중복 처리 방지 플래그 (capture() 시 리셋)
let recoveryHandled = false

// Process death 후 복구된 사진 처리
function handleCaptureRecovery(event: {
  resourcePath: string
  shapeDatabaseId: string
  recovered: boolean
}): void {
  if (!event || !event.resourcePath) return
  if (recoveryHandled) return
  recoveryHandled = true
  clearPendingCapture()
  console.info('카메라 촬영 복구:', event.resourcePath, 'shapeId:', event.shapeDatabaseId)
  saveRecoveredCapture(event.resourcePath, event.shapeDatabaseId)
  // Photo.vue가 이미 마운트된 상태일 수 있으므로 커스텀 이벤트로 즉시 알림
  window.dispatchEvent(new Event('zentrix:captureRecovered'))
  // push 이벤트로 처리된 경우 native SharedPreferences도 소비하여 재시작 시 중복 방지
  modalCamera.checkRecoveredCapture().catch(() => {})
}

// 카메라 이벤트 처리
function handleCameraEvent(_event: CameraEvent): void {
  return //TODO: 이제 shape와 다른 사진간의 이벤트 분리 필요
  // if (!event) return
  //
  // try {
  //   console.info('카메라 이벤트 수신:', event)
  //   const deviceEventLog = useDeviceEventLogStore()
  //
  //   // 이벤트 타입에 따라 다른 로깅 처리
  //   if (event.type === 'DEFECT_PHOTO_CAPTURE') {
  //     // event_type 필드를 이용하여 정확한 이벤트 타입으로 로깅
  //     const eventType = event.event_type || 'shape.camera.capture' // 기본값 설정
  //
  //     deviceEventLog.addEventLogAll(
  //       [
  //         {
  //           type: eventType, // 이벤트 타입 사용
  //           related_id: event.shape_id, // 결함 ID
  //           new_value: event.file_path || '', // 파일 경로 (버튼 클릭 이벤트에서는 빈 값일 수 있음)
  //           old_value: 'null', // 이전 값 (없음)
  //         },
  //       ],
  //       'shape',
  //     )
  //
  //     console.info(`카메라 이벤트 로그 생성 완료: ${eventType}`)
  //   }
  // } catch (error) {
  //   console.error('카메라 이벤트 처리 중 오류 발생:', error)
  // }
}

// 앱 종료 시 리스너 제거
export async function cleanupCameraSystem(): Promise<void> {
  if (cameraEventListener) {
    await cameraEventListener.remove()
    cameraEventListener = null
  }
  if (captureRecoveryListener) {
    await captureRecoveryListener.remove()
    captureRecoveryListener = null
  }
  recoveryHandled = false
}
